<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <!-- Login v1 -->
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <h2 class="brand-text text-primary">
            <img :src="logo" alt="RättVisat logotyp" />
            RättVisat
          </h2>
        </b-link>

        <b-card-title class="mb-1">
          Välkommen till RättVisat!
        </b-card-title>
        <b-card-text class="mb-2">
          Logga in för att börja använda appen.
        </b-card-text>

        <!-- form -->
        <validation-observer ref="loginForm" #default="{invalid}">
          <b-form class="auth-login-form mt-2" @submit.prevent>
            <!-- email -->
            <b-form-group label-for="email" label="Mailadress">
              <validation-provider
                #default="{ errors }"
                name="Mailadress"
                rules="required|email"
              >
                <b-form-input
                  id="email"
                  v-model="user.email"
                  name="login-email"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Mailadress..."
                  autofocus
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- password -->
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label for="password">Lösenord</label>
                <b-link :to="{ name: 'forgot-password' }">
                  <small>Glömt lösenord?</small>
                </b-link>
              </div>
              <validation-provider
                #default="{ errors }"
                name="Lösenord"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="password"
                    v-model="user.password"
                    :type="passwordFieldType"
                    class="form-control-merge"
                    :state="errors.length > 0 ? false : null"
                    name="login-password"
                    placeholder="Lösenord"
                  />

                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-alert
              v-if="connectedToDabase && invalidCredentials"
              show
              variant="danger"
              class="mb-2"
            >
              <div class="alert-body">
                <i class="fas fa-exclamation-triangle"></i> Fel mailadress eller
                lösenord
              </div>
            </b-alert>
            <b-alert
              v-if="!connectedToDabase"
              show
              variant="danger"
              class="mb-2"
            >
              <div class="alert-body">
                <i class="fas fa-exclamation-triangle"></i> Kunde inte ansluta
                till databasen
              </div>
            </b-alert>
            <!-- checkbox -->
            <!-- <b-form-group>
              <b-form-checkbox
                id="remember-me"
                v-model="status"
                name="checkbox-1"
              >
                Håll mig inloggad
              </b-form-checkbox>
            </b-form-group> -->

            <!-- submit button -->
            <b-button
              @click="login"
              variant="primary"
              type="submit"
              block
              :disabled="invalid"
            >
              Logga in
            </b-button>
          </b-form>
        </validation-observer>

        <b-card-text class="text-center mt-2">
          <span>Har du inget konto? </span>
          <b-link v-if="!code" :to="{ name: 'register' }">
            <span>Skapa ett konto här!</span>
          </b-link>
          <b-link
            v-else
            :to="{ name: 'registerWithCode', params: { code: code } }"
          >
            <span>Skapa ett konto här!</span>
          </b-link>
        </b-card-text>

        <!-- <div class="divider my-2">
          <div class="divider-text">
            eller logga in med
          </div>
        </div>
        <div class="auth-footer-btn d-flex justify-content-center">
          <b-button
            href="javascript:void(0)"
            variant="secondary"
            block
          >
          Mobilt BankID
          </b-button>
        </div> -->
      </b-card>
      <!-- /Login v1 -->
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardTitle,
  BCardText,
  BInputGroup,
  BInputGroupAppend,
  BAlert,
} from "bootstrap-vue";
import { required, email } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import RättVisatLogo from "../assets/images/icons/login/RattVisatLogo.svg";

export default {
  components: {
    // BSV
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BLink,
    BCardText,
    BInputGroup,
    BAlert,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  props: {
    code: String,
  },
  data() {
    return {
      user: {},
      status: "",
      // validation rules
      required,
      email,
      locale: "sv",
      connectedToDabase: true,
      invalidCredentials: false,
      logo: RättVisatLogo,
    };
  },
  mounted() {
    localize(this.locale);
  },
  methods: {
    userNotification: function(title, message, icon) {
      this.$swal({
        title: title,
        text: message,
        icon: icon,
        confirmButtonText: "Ok",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    login() {
      if (this.user.password && this.user.email) {
        try {
          this.$store
            .dispatch("login", { user: this.user, code: this.code })
            .then((response) => {
              if (response === "ERRNOTVERIFIED0152") {
                this.userNotification(
                  "Inte verifierad",
                  "Din mailadress är inte verifierad, vänligen kolla din mail efter verifieringsmailet",
                  "error"
                );
                return;
              }

              if (!(response.status >= 300)) {
                if (response.isOwnerOfPortfolio) {
                  this.$router.push("/" + response.homepage.url);
                } else {
                  this.$router.push("/Mitt-konto");
                }
              } else {
                this.invalidCredentials = true;
              }
            });
        } catch (e) {
          if (e.status) {
            this.invalidCredentials = true;
            return;
          }
          this.connectedToDabase = false;
        }
      }
    },
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
